import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import lz from "lzutf8";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

export const A = import.meta.env.VITE_ASSET_BASE_URL;

export type TranslationFn = (key: string) => string;

export type N = Record<string, string | number | boolean | undefined | null | string[] | object>;

export function enc(obj: object): string {
  return lz.encodeBase64(lz.compress(JSON.stringify(obj)));
}

export function pv(key: string) {
  return [{ key, props: {}, _owner: null, _store: {} }];
}

// --- Constants ---

export const SOCIAL_ICONS = [
  { platform: "facebook", url: "https://facebook.com" },
  { platform: "twitter", url: "https://twitter.com" },
  { platform: "instagram", url: "https://instagram.com" },
  { platform: "linkedin", url: "https://linkedin.com" },
];

export const SPEAKER_ICONS = [{ icon: "IconPhone" }, { icon: "IconMail" }, { icon: "IconWorld" }];

export const PAD = "p-6 wb-md:p-10 wb-lg:p-16";
export const PAD_CARD = "p-4 wb-md:p-6 wb-lg:p-8";
export const DIST_CARD = "space-y-1 wb-md:space-y-2 wb-lg:space-y-3";
export const BTN_FORM_SP = "mt-3 wb-md:mt-4 wb-lg:mt-5";
export const SEC_MARGIN = "py-6 wb-md:py-8 wb-lg:py-12";

export const AUTH_WRAPPER_PROPS = {
  padding: PAD, sectionMargin: "py-0", isVisible: true,
  borderRadius: "rounded-none", border: false, borderWidth: "border-2", borderStyle: "border-solid",
  borderSides: [] as string[], isVisibleImage: false, imageLayout: "none", backgroundOverlayColor: "#00000080",
  sectionHeight: 80, contentAlignment: "center",
};

export const AUTH_CHILD_PROPS = {
  isVisible: true, padding: PAD_CARD, distTexts: DIST_CARD,
  formTextSpacing: BTN_FORM_SP, fieldSpacing: "medium", buttonFormSpacing: BTN_FORM_SP, buttonAlign: "left",
  border: true, borderWidth: "border", borderStyle: "border-solid",
  borderSides: ["top", "right", "bottom", "left"],
};

const AGENDA_DATA = [
  { title: "Opening Keynote & Welcome", speaker: "Speaker: Jane Smith", desc: "A deep dive into the latest trends and developments. This session covers practical strategies and real-world case studies from leading experts in the field." },
  { title: "Panel Discussion: Future of Events", speaker: "Speaker: Michael Chen", desc: "Industry leaders share their perspectives on emerging trends, audience engagement, and the evolving landscape of live experiences." },
  { title: "Workshop: Hands-On Strategies", speaker: "Speaker: Sarah Johnson", desc: "An interactive workshop where attendees collaborate on real-world challenges and develop actionable plans they can implement immediately." },
  { title: "Networking & Closing Remarks", speaker: "Speaker: David Park", desc: "Connect with fellow attendees over refreshments, followed by a recap of key takeaways and a preview of upcoming events." },
];

// Faces come from the shared sample rotation by slot (see
// lib/website-builder/sampleContent) — the old per-row `img: "03"` pointed at
// shared/speaker03.jpg & co, which are event-detail photos (a microphone, a
// leather folio), not people.
const SPEAKER_DATA = [
  { name: "Jane Smith", occupation: "CEO, Example Inc." },
  { name: "Michael Chen", occupation: "CTO, Digital Ventures" },
  { name: "Sarah Johnson", occupation: "Head of Marketing, CreativeHub" },
  { name: "David Park", occupation: "Director of Events, Global Connect" },
];

const TESTIMONIAL_DATA = [
  { quote: "\u201CThis event completely exceeded my expectations. The speakers were world-class and the networking opportunities were invaluable.\u201D", ...SPEAKER_DATA[0] },
  { quote: "\u201CThe workshops were incredibly hands-on and practical. I left with a clear action plan that I could implement the very next day.\u201D", ...SPEAKER_DATA[1] },
  { quote: "\u201CFantastic organization from start to finish. The connections I made here have already led to two new partnerships for my business.\u201D", ...SPEAKER_DATA[2] },
  { quote: "\u201CEvery session was packed with valuable insights. This is the one event I never miss \u2014 it keeps getting better each year.\u201D", ...SPEAKER_DATA[3] },
];

const BLOG_DATA = [
  { topic: "<b>Getting Ready for the Big Day</b>", content: "Here's everything you need to know to make the most of your experience, from travel tips to must-attend sessions." },
  { topic: "<b>Top 5 Sessions You Can't Miss</b>", content: "From inspiring keynotes to hands-on workshops, we've curated a list of the sessions that will leave the biggest impact on your career." },
  { topic: "<b>Meet Our Speakers</b>", content: "Get to know the thought leaders and industry experts who will be sharing their insights and experiences on stage this year." },
  { topic: "<b>Venue & Travel Guide</b>", content: "Everything you need to know about getting to the venue, nearby hotels, parking options, and local dining recommendations." },
];

const FAQ_DATA = [
  { q: "<b>What is included in my ticket?</b>", a: "Your ticket includes full access to all sessions, workshops, networking areas, and complimentary refreshments throughout the event." },
  { q: "<b>Is there a dress code?</b>", a: "The event is business casual. We recommend comfortable attire as you may be moving between sessions and workshops throughout the day." },
  { q: "<b>Can I get a refund if I can't attend?</b>", a: "Full refunds are available up to 14 days before the event. After that, tickets can be transferred to another attendee at no extra cost." },
  { q: "<b>Where can I park?</b>", a: "On-site parking is available on a first-come, first-served basis. We also recommend public transport \u2014 the venue is a 5-minute walk from the main station." },
  { q: "<b>Will the sessions be recorded?</b>", a: "Yes, all main stage sessions will be recorded and made available to ticket holders within one week after the event." },
];

const NUMBERS_DATA = [
  { num: "<b>500+</b>", desc: "Attendees" },
  { num: "<b>20+</b>", desc: "Speakers" },
  { num: "<b>3</b>", desc: "Days" },
  { num: "<b>15</b>", desc: "Workshops" },
];

// --- Leaf node factories ---

export function tc(parent: string, p: N): N {
  return {
    type: { resolvedName: "TextChild" }, isCanvas: false, props: p,
    displayName: "TextChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

const BTN_DEFAULTS = {
  type: "primary", size: "medium", buttonType: "button",
  disabled: false, loading: false, underline: false, margin: "", isVisible: true, border: true,
};

export function btn(parent: string, p: N): N {
  return {
    type: { resolvedName: "Button" }, isCanvas: false, props: { ...BTN_DEFAULTS, ...p },
    displayName: "ButtonChild", custom: { icon: "IconMouse2" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

export const ACCEPTANCE_VISIBILITY_FILTER = {
  type: "&", simpleFilters: [] as { field: string; operator: string; value: string | string[] }[],
  advancedFilters: [{ type: "&" as const, simpleFilters: [{ field: "state", operator: "=", value: "ACCEPTED" }] }],
};

const FI_DEFAULTS = {
  isVisible: true, canDuplicate: false, hoverMessage: "", labelFontSize: "text-md",
  multilineRows: 8, chapterSpacingBottom: "mb-4 wb-md:mb-6 wb-lg:mb-6",
  chapterSpacingTop: "mt-4 wb-md:mt-6 wb-lg:mt-6", inputFontSize: "text-sm",
  border: true, multiline: false, displayName: "FormInputChild", gridArea: "col-span-6", changeable: true,
};

export function fi(parent: string, p: N): N {
  return {
    type: { resolvedName: "FormInput" }, isCanvas: false, props: { ...FI_DEFAULTS, ...p },
    displayName: "FormInputChild", custom: { icon: "IconForms" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

/**
 * The participation ("Teilnahme") question node for a DataInput fields group.
 *
 * AUDIT 2026-08-14. Until today NO template, theme, wizard injection or DSL
 * compiler emitted this node — a repo-wide search for a serialized
 * `inputFieldVariable: "state"` returned nothing. The runtime's acceptance
 * widget then fell back to submitting under `acceptanceState`, a key the
 * worker drops on purpose, so every Zusage given on a generated site was
 * discarded silently: no state written, no "Gast hat zugesagt" automation, no
 * history row. The other fields were meanwhile gated on `state = ACCEPTED` —
 * i.e. the templates asked a question they never shipped the control for.
 *
 * The RUNTIME renders no FormInput for this node (formInput.tsx returns null
 * for an acceptance variable; DataInputAcceptanceField draws the real widget).
 * It exists to DECLARE the question: it carries the button labels and colours,
 * and it puts the variable into `fieldLabelMap`, which is what the "nothing
 * changed" resubmit check compares against.
 *
 * ⚠ NO `visibilityFilter`, ever. Gating the question on `state = ACCEPTED`
 * would make it gate itself: the server's `stripHiddenValues` evaluates that
 * rule against a submitted `state = DECLINED`, resolves HIDDEN, and deletes
 * the Absage out of the submission. `buildFormInputProps` in the DataInput
 * panel makes the same exclusion for the same reason.
 */
export function acceptanceFi(parent: string, label?: string): N {
  return fi(parent, {
    // Optional: the runtime's DataInputAcceptanceField draws its own two
    // buttons and never renders this label — the only consumer is the
    // overview/summary map. The DSL lane (buildWebsiteCraftJsFromBlocks) has
    // no translator in scope, and omitting the prop is better than hardcoding
    // one language into a builder that is explicitly multi-locale.
    ...(label !== undefined ? { label } : {}),
    inputFieldVariable: "state",
    requiredField: true,
    gridArea: "col-span-12",
  });
}

// --- Child node factories ---

function agendaChild(parent: string, i: number, time: string): N {
  const d = AGENDA_DATA[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.speaker, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function blogChild(parent: string, i: number, td: TranslationFn): N {
  const d = BLOG_DATA[i];
  return {
    type: { resolvedName: "BlogChild" }, isCanvas: false,
    props: {
      iconAlignment: "left", isVisible: true,
      isVisibleTopic: true, textTopic: d.topic, fontSizeTopic: "text-lg", textAlignTopic: "left",
      isVisibleContent: true, textContent: d.content, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleButton: false, size: "small", text: td("learnMore"), underline: false, border: true,
    },
    displayName: "BlogChild", custom: { icon: "IconArticle" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function speakerChild(parent: string, i: number): N {
  const d = SPEAKER_DATA[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-xl", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.occupation, fontSizeOccupation: "text-md", textAlignOccupation: "left",
      icons: SPEAKER_ICONS, isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function testimonialChild(parent: string, i: number): N {
  const d = TESTIMONIAL_DATA[i];
  return {
    type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src,
      imageUrlBottom: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
      isVisibleImageTop: true, imageBottomBackground: "transparent", isVisibleImageBottom: true,
      isVisibleContent: true, textContent: d.quote, fontSizeContent: "text-md", textAlignContent: "left",
      isVisibleName: true, textName: d.name, fontSizeName: "text-xl", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.occupation, fontSizeOccupation: "text-md", textAlignOccupation: "left",
    },
    displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function numbersChild(parent: string, i: number): N {
  const d = NUMBERS_DATA[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-lg", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-md", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(parent: string, i: number): N {
  const d = FAQ_DATA[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function galleryImage(parent: string, num: string): N {
  return {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: `${A}/shared/events${num}.jpg`, isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

export function emptyFieldsGroup(parent: string, groupIndex: number): N {
  return {
    type: { resolvedName: "FieldsGroup" }, isCanvas: true,
    props: { isVisible: false, fieldCount: 0, groupIndex, fieldSpacing: "medium" },
    displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// --- Page scaffold ---

export function scaffold(
  ids: { ab: string; abs: string; body: string; ft: string; fts: string },
  bodyNodes: string[], rootCanvas: boolean, rootProps: N = {},
): N {
  return {
    ROOT: {
      type: { resolvedName: "PageWrapper" }, isCanvas: rootCanvas, props: rootProps,
      displayName: "PageWrapper", custom: {}, hidden: false,
      nodes: [ids.ab, ids.body, ids.ft], linkedNodes: {},
    },
    [ids.ab]: {
      type: { resolvedName: "AppBar" }, isCanvas: false, props: {},
      displayName: "appbar", custom: {}, parent: "ROOT", hidden: false,
      nodes: [ids.abs], linkedNodes: {},
    },
    [ids.abs]: "AppBarSection" as unknown as N,
    [ids.body]: {
      type: { resolvedName: "Body" }, isCanvas: true, props: {},
      displayName: "body", custom: {}, parent: "ROOT", hidden: false,
      nodes: bodyNodes, linkedNodes: {},
    },
    [ids.ft]: {
      type: { resolvedName: "Footer" }, isCanvas: false, props: {},
      displayName: "footer", custom: {}, parent: "ROOT", hidden: false,
      nodes: [ids.fts], linkedNodes: {},
    },
    [ids.fts]: "FooterSection" as unknown as N,
  };
}

// ============================================================
// Exported template functions
// ============================================================

export const websiteJsonNewPage = (td: TranslationFn): string => {
  const S = "fI1OW7-TpS"; // TextSection ID
  const H = "eIVdw2Fu8u"; // Hero parent (orphaned in this template)
  return enc({
    ...scaffold(
      { ab: "DCH9WIIT55", abs: "wv1hhkX0VF", body: "a-BmKl-_BS", ft: "xaUW2UDZT8", fts: "D68mdQr62U" },
      [S], false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-md" },
    ),
    [S]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, textSpacing: "space-y-3", buttonTextSpacing: "mt-8", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("textSectionHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("textSectionText"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("textSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("textPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: "a-BmKl-_BS", hidden: false, nodes: [],
      linkedNodes: { headline: "VyVj3trCsf", header: "zmI2OpamIT", text: "_Sb_Af5eTS", buttonOne: "R3yY02ftGK", buttonTwo: "n2HCjrq-We" },
    },
    // Hero orphaned children (parent eIVdw2Fu8u not in this template)
    inLmei1sej: tc(H, { isVisible: false, text: "...", fontSize: "text-xl", textAlign: "center", displayName: "headline", color: "#FFFFFF", header: true }),
    QQ8zq2IESf: tc(H, { isVisible: true, text: td("textSectionHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", color: "#FFFFFF", header: true }),
    P3b4eNGuh3: tc(H, { isVisible: true, text: td("heroText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: "#FFFFFF", header: false }),
    VQAhZ06eVa: btn(H, { type: "secondary", text: td("heroSecondaryButton"), displayName: "buttonOne" }),
    "36aF068c1p": btn(H, { type: "primary", text: td("heroPrimaryButton"), displayName: "buttonTwo" }),
    // TextSection children
    VyVj3trCsf: tc(S, { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    zmI2OpamIT: tc(S, { isVisible: true, text: td("textSectionHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    _Sb_Af5eTS: tc(S, { isVisible: true, text: td("textSectionText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    R3yY02ftGK: btn(S, { type: "secondary", text: td("textSecondaryButton"), displayName: "buttonOne" }),
    "n2HCjrq-We": btn(S, { text: td("textPrimaryButton"), displayName: "buttonTwo" }),
  });
};

export const appbar = (): string => {
  return enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true, border: true,
      isLogoVisible: true, logoSrc: `${A}/shared/logo.svg`, logoWidth: "w-32", logoHeight: "h-16", logoPosition: "left",
      isLinksVisible: false, links: [], linksPosition: "center", linksFontSize: "text-md", linksFontWeight: "font-medium",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });
};

export const footer = (td: TranslationFn): string => {
  return enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-8 wb-md:px-6 wb-lg:px-8", isVisible: true, borderTop: "border-t border-gray-200",
      isLogoVisible: true, logoSrc: `${A}/shared/logo.svg`, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: false, linkGroups: [],
      linksTitleFontSize: "text-lg", linksTitleFontWeight: "font-semibold",
      linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:text-gray-900",
      isSocialVisible: true, socialIcons: SOCIAL_ICONS, socialHoverColor: "hover:text-gray-900", socialSize: 24,
      socialTitle: td("followUs"),
      // FALSE until a real toggle exists. The runtime renders this block gated on
      // `isContactVisible`, and the only place the flag is ever set is right here
      // — the builder shows the node hidden and offers no switch, so an organiser
      // can neither see it nor turn it off. Seeded `true`, every default-template
      // site published a "Contact Us" block with clickable mailto:/tel: links to
      // the placeholder values below: info@company.com, +1 (123) 456-7890,
      // 123 Street Name. Invented contact details on a live event site.
      isContactVisible: false, contactTitle: td("contactUs"),
      contactEmail: td("placeholderEmail"), contactPhone: td("placeholderPhone"), contactAddress: td("placeholderAddress"),
      contactTitleFontSize: "text-lg", contactTitleFontWeight: "font-semibold",
      isCopyrightVisible: true, copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });
};

export const agenda = (td: TranslationFn) => {
  const SEC = "ag_agendaSection";
  const TIME = "<b>XX:XX - YY:YY</b>";
  return enc({
    ...scaffold({ ab: "ag_appbar", abs: "ag_appbarSection", body: "ag_body", ft: "ag_footer", fts: "ag_footerSection" }, [SEC], true),
    [SEC]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        sectionHeight: 80, contentAlignment: "center",
        padding: PAD, textSpacing: "space-y-4", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("eventScheduleHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("eventScheduleText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda",
      custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: "ag_body", hidden: false,
      nodes: ["ag_child1", "ag_child2", "ag_child3", "ag_child4"],
      linkedNodes: { headline: "ag_headline", header: "ag_header", text: "ag_text" },
    },
    ag_headline: tc(SEC, { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    ag_header: tc(SEC, { isVisible: true, text: td("eventScheduleHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    ag_text: tc(SEC, { isVisible: true, text: td("eventScheduleText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    ag_child1: agendaChild(SEC, 0, TIME),
    ag_child2: agendaChild(SEC, 1, TIME),
    ag_child3: agendaChild(SEC, 2, TIME),
    ag_child4: agendaChild(SEC, 3, TIME),
  });
};

export const contact = (td: TranslationFn) => {
  const SEC = "z8do-YZE-s";
  return enc({
    ...scaffold({ ab: "Ed1CeFq3pN", abs: "HRJF1c-Epq", body: "rYqyGgOEvG", ft: "y-r5fqVBFA", fts: "TqbrOOn0lV" }, [SEC], true),
    [SEC]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center",
        infoSection: true, backgroundColor: "transparent", padding: PAD,
        textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "100%", infoPosition: "left/top",
        isVisibleHeadline: false, textHeadline: td("contactSectionHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("contactSectionHeader"), fontSizeHeader: "text-xl", textAlignHeader: "left",
        isVisibleText: false, textText: td("enterDetailsText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: false, socialIcons: SOCIAL_ICONS, socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("sendMessage"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact",
      custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: "rYqyGgOEvG", hidden: false, nodes: [],
      linkedNodes: {
        headline: "hnzh27BNVZ", header: "YNVfIW3wI6", text: "WZGtd6JiG3",
        firstName: "xMxQ0kadAW", lastName: "ZpqJgjqjHz", eMail: "RGjIqYzx3V",
        phoneNumber: "cSugSkfpls", Message: "HI7ONpYs62", button: "ubSUYGC9F6",
      },
    },
    hnzh27BNVZ: tc(SEC, { isVisible: false, text: td("contactSectionHeadline"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    YNVfIW3wI6: tc(SEC, { isVisible: true, text: td("contactSectionHeader"), fontSize: "text-xl", textAlign: "left", displayName: "header", header: true }),
    WZGtd6JiG3: tc(SEC, { isVisible: false, text: td("enterDetailsText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    xMxQ0kadAW: fi(SEC, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    ZpqJgjqjHz: fi(SEC, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    RGjIqYzx3V: fi(SEC, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    cSugSkfpls: fi(SEC, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    HI7ONpYs62: fi(SEC, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    ubSUYGC9F6: btn(SEC, { linkType: "internal", linkUrl: "", text: td("sendMessage"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

export const register = (td: TranslationFn) => {
  const REG = "R_0DH8juUb";
  const RC = "RegChild_01";
  return enc({
    ...scaffold({ ab: "0r1YXd3O4_", abs: "y6rH7O4U04", body: "Uek_UH3lMk", ft: "ou5bf_sfz7", fts: "SskA8CLVjz" }, [REG], true),
    [REG]: {
      type: { resolvedName: "Register" }, isCanvas: false, props: AUTH_WRAPPER_PROPS,
      displayName: "Register", custom: { icon: "IconUserPlus", preview: pv("Register-preview") },
      parent: "Uek_UH3lMk", hidden: false, nodes: [], linkedNodes: { registerChild: RC },
    },
    [RC]: {
      type: { resolvedName: "RegisterChild" }, isCanvas: true,
      props: { ...AUTH_CHILD_PROPS, fieldCount: 3 },
      displayName: "RegisterChild", custom: { icon: "IconUserPlus", allowedComponents: ["FormInputChild"] },
      parent: REG, hidden: false, nodes: ["Reg_firstName", "Reg_lastName", "Reg_email"],
      linkedNodes: { headline: "GejNgIQhvH", header: "fKU0VenOZx", text: "O8kdq_14Hu", primary: "8JVSKO664R", secondary: "KVulrXKshE" },
    },
    GejNgIQhvH: tc(RC, { isVisible: false, text: td("registerHeadline"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    fKU0VenOZx: tc(RC, { isVisible: true, text: td("registerHeader"), fontSize: "text-3xl", textAlign: "left", displayName: "header", color: "#ffffff", header: true }),
    O8kdq_14Hu: tc(RC, { isVisible: true, text: td("enterDetailsText"), fontSize: "text-md", textAlign: "left", displayName: "text", color: "#cbd5e1", header: false }),
    Reg_firstName: fi(RC, { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", changeable: true }),
    Reg_lastName: fi(RC, { label: td("lastName"), requiredField: false, inputFieldVariable: "lastName", changeable: true }),
    Reg_email: fi(RC, { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", changeable: true }),
    "8JVSKO664R": btn(RC, { linkType: "internal", linkUrl: "", text: td("createAccount"), widthMode: "full", buttonType: "submit", displayName: "buttonOne", fixedNavigation: false }),
    KVulrXKshE: btn(RC, { linkType: "internal", type: "secondary", linkUrl: "/login", text: td("alreadyHaveAccount"), widthMode: "full", underline: true, border: false, displayName: "buttonTwo", fixedNavigation: true }),
  });
};

export const welcome = (td: TranslationFn) => {
  const B = "a-BmKl-_BS";
  const TIME_UHR = "<b>XX:XX Uhr - YY:YY Uhr</b>";

  return enc({
    ...scaffold(
      { ab: "DCH9WIIT55", abs: "wv1hhkX0VF", body: B, ft: "xaUW2UDZT8", fts: "D68mdQr62U" },
      ["eIVdw2Fu8u", "fI1OW7-TpS", "DI_Section1", "_zqBkpMWrY", "_5yKRYbJiW", "En2q0YhVdO", "EbteG5ufZa", "v9xUhfuvdA", "wrJx_nHRZi", "kANaBb_u2g", "aFVOnX6KJc", "BR_UaZSeQm", "OutHHVpX4o", "5RlDSMNjqM"],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-md" },
    ),

    // ── Hero ──
    eIVdw2Fu8u: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 80, contentAlignment: "center",
        buttonAlign: "center", padding: "px-6 wb-md:px-8 wb-lg:px-12", textSpacing: "space-y-3", buttonTextSpacing: "mt-8", isVisible: true,
        isVisibleHeadline: false, textHeadline: "...", fontSizeHeadline: "text-xl", textAlignHeadline: "center", headlineColor: "#FFFFFF",
        isVisibleHeader: true, textHeader: td("yourEventStartsHere"), fontSizeHeader: "text-2xl", textAlignHeader: "center", headerColor: "#FFFFFF",
        isVisibleText: true, textText: td("heroText"), fontSizeText: "text-md", textAlignText: "center", textColor: "#FFFFFF",
        secondaryButtonSize: "medium", secondaryButtonText: td("heroSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonBorder: true,
        primaryButtonText: td("heroPrimaryButton"), isVisiblePrimaryButton: true, primaryButtonUnderline: false,
        imageLayout: "background", backgroundOverlayColor: "#00000080",
        imageSrc: `${A}/shared/hero.jpg`, backgroundColor: "#f5f5f5",
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "inLmei1sej", header: "QQ8zq2IESf", text: "P3b4eNGuh3", buttonOne: "VQAhZ06eVa", buttonTwo: "36aF068c1p" },
    },
    inLmei1sej: tc("eIVdw2Fu8u", { isVisible: false, text: "...", fontSize: "text-xl", textAlign: "center", displayName: "headline", color: "#FFFFFF", header: true }),
    QQ8zq2IESf: tc("eIVdw2Fu8u", { isVisible: true, text: td("textSectionHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", color: "#FFFFFF", header: true }),
    P3b4eNGuh3: tc("eIVdw2Fu8u", { isVisible: true, text: td("heroText"), fontSize: "text-md", textAlign: "center", displayName: "text", color: "#FFFFFF", header: false }),
    VQAhZ06eVa: btn("eIVdw2Fu8u", { type: "secondary", text: td("heroSecondaryButton"), displayName: "buttonOne" }),
    "36aF068c1p": btn("eIVdw2Fu8u", { type: "primary", text: td("heroPrimaryButton"), displayName: "buttonTwo" }),

    // ── TextSection ──
    "fI1OW7-TpS": {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3", buttonTextSpacing: "mt-8", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("textSectionHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("textSectionText"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("textSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("textPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "VyVj3trCsf", header: "zmI2OpamIT", text: "_Sb_Af5eTS", buttonOne: "R3yY02ftGK", buttonTwo: "n2HCjrq-We" },
    },
    VyVj3trCsf: tc("fI1OW7-TpS", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    zmI2OpamIT: tc("fI1OW7-TpS", { isVisible: true, text: td("textSectionHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    _Sb_Af5eTS: tc("fI1OW7-TpS", { isVisible: true, text: td("textSectionText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    R3yY02ftGK: btn("fI1OW7-TpS", { type: "secondary", text: td("textSecondaryButton"), displayName: "buttonOne" }),
    "n2HCjrq-We": btn("fI1OW7-TpS", { text: td("textPrimaryButton"), displayName: "buttonTwo" }),

    // ── DataInput ──
    DI_Section1: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("dataInputHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("dataInputHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("dataInputText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: false,
        multiStepEnabled: false, stepLabels: [], multiStepMode: "pages",
        overviewEnabled: false, stepSections: [["fields-0", "companions"]],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "DI_headline1", header: "DI_header1", text: "DI_text1",
        fieldsGroup0: "DI_fields0", fieldsGroup1: "DI_fields1", fieldsGroup2: "DI_fields2",
        fieldsGroup3: "DI_fields3", fieldsGroup4: "DI_fields4",
        companionsGroup: "DI_companions1", overviewGroup: "DI_ovGroup1", checkboxes: "DI_checkboxes1",
        button: "DI_button1", backButton: "DI_backBtn1", nextButton: "DI_nextBtn1",
      },
    },
    DI_headline1: tc("DI_Section1", { isVisible: true, text: td("dataInputHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    DI_header1: tc("DI_Section1", { isVisible: true, text: td("dataInputHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    DI_text1: tc("DI_Section1", { isVisible: true, text: td("dataInputText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    DI_fields0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 4, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: "DI_Section1", hidden: false, nodes: ["DI_f0_acceptance", "DI_f0_firstName", "DI_f0_lastName", "DI_f0_email"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other field in
    // it is gated on. See `acceptanceFi`.
    DI_f0_acceptance: acceptanceFi("DI_fields0", td("acceptanceState")),
    DI_f0_firstName: fi("DI_fields0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    DI_f0_lastName: fi("DI_fields0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    DI_f0_email: fi("DI_fields0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    DI_fields1: emptyFieldsGroup("DI_Section1", 1),
    DI_fields2: emptyFieldsGroup("DI_Section1", 2),
    DI_fields3: emptyFieldsGroup("DI_Section1", 3),
    DI_fields4: emptyFieldsGroup("DI_Section1", 4),
    DI_companions1: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: "DI_Section1", hidden: false, nodes: ["DI_comp_firstName"], linkedNodes: {},
    },
    DI_comp_firstName: fi("DI_companions1", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    DI_ovTitle1: tc("DI_ovGroup1", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    DI_ovText1: tc("DI_ovGroup1", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    DI_ovGroup1: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: "DI_Section1", stepSections: [["fields-0", "companions"]], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: "DI_Section1", hidden: false, nodes: ["DI_ovTitle1", "DI_ovText1"], linkedNodes: {},
    },
    DI_checkboxes1: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: "DI_Section1", hidden: false, nodes: [], linkedNodes: {},
    },
    DI_button1: btn("DI_Section1", { linkType: "internal", linkUrl: "", text: td("submitButton"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    DI_backBtn1: btn("DI_Section1", { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    DI_nextBtn1: btn("DI_Section1", { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Countdown ──
    _zqBkpMWrY: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN,
        padding: PAD, isVisible: false, textSpacing: "space-y-3", buttonTextSpacing: "mt-6", buttonAlign: "center",
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("countdownSectionHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("countdownSectionText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("countdownSectionButton"), isVisibleButton: false, buttonBorder: true, buttonUnderline: false,
        countdownWidth: "100%", countdownPosition: "right/bottom", countdownVariant: "separated",
        isVisiblCountdown: true, countdownSizeDate: "text-xl", countdownSize: "text-base",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "JN17P3LMsT", header: "y0HTpfOICi", text: "juO4tBo2kg", button: "O4iH_G7N81" },
    },
    JN17P3LMsT: tc("_zqBkpMWrY", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", fontFamily: "Tahoma, Geneva, sans-serif", textAlign: "center", displayName: "headline", header: true }),
    y0HTpfOICi: tc("_zqBkpMWrY", { isVisible: true, text: td("countdownSectionHeader"), fontSize: "text-xl", fontFamily: "Tahoma, Geneva, sans-serif", textAlign: "center", displayName: "header", header: true }),
    juO4tBo2kg: tc("_zqBkpMWrY", { isVisible: true, text: td("countdownSectionText"), fontSize: "text-md", fontFamily: "Inter, system-ui, sans-serif", textAlign: "center", displayName: "text", header: false }),
    O4iH_G7N81: btn("_zqBkpMWrY", { text: td("countdownSectionButton"), isVisible: false, displayName: "ButtonChild" }),

    // ── Agenda ──
    _5yKRYbJiW: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-4", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("eventScheduleHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("eventScheduleText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["AA7YfQONMH", "wSvyEnnAOd", "BnqY-dAEdQ", "jcsfw3bFOz"],
      linkedNodes: { headline: "bfkb2t0L-v", header: "ObwnZFnI6b", text: "TOb-4LVuQ8" },
    },
    "bfkb2t0L-v": tc("_5yKRYbJiW", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    ObwnZFnI6b: tc("_5yKRYbJiW", { isVisible: true, text: td("eventScheduleHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    "TOb-4LVuQ8": tc("_5yKRYbJiW", { isVisible: true, text: td("eventScheduleText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    AA7YfQONMH: agendaChild("_5yKRYbJiW", 0, TIME_UHR),
    wSvyEnnAOd: agendaChild("_5yKRYbJiW", 1, TIME_UHR),
    "BnqY-dAEdQ": agendaChild("_5yKRYbJiW", 2, TIME_UHR),
    jcsfw3bFOz: agendaChild("_5yKRYbJiW", 3, TIME_UHR),

    // ── Blog ──
    En2q0YhVdO: {
      type: { resolvedName: "Blog" }, isCanvas: true,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("blogHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("blogText"), fontSizeText: "text-md", textAlignText: "center",
        blogPosition: "bottom",
      },
      displayName: "Blog", custom: { icon: "IconClick", preview: pv("Blog-preview"), allowedComponents: ["BlogChild"] },
      parent: B, hidden: false, nodes: ["luIkZ8WkQ-", "f0BIVD-cz8", "egq4HrEM09", "vJVgMh4mt1"],
      linkedNodes: { headline: "zEAGb43XtN", header: "wDUUUfZH_t", text: "__lxJ9iRaa" },
    },
    zEAGb43XtN: tc("En2q0YhVdO", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    wDUUUfZH_t: tc("En2q0YhVdO", { isVisible: true, text: td("blogHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    __lxJ9iRaa: tc("En2q0YhVdO", { isVisible: true, text: td("blogText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    "luIkZ8WkQ-": blogChild("En2q0YhVdO", 0, td),
    "f0BIVD-cz8": blogChild("En2q0YhVdO", 1, td),
    egq4HrEM09: blogChild("En2q0YhVdO", 2, td),
    vJVgMh4mt1: blogChild("En2q0YhVdO", 3, td),

    // ── Speaker ──
    EbteG5ufZa: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("speakerHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("speakerText"), fontSizeText: "text-md", textAlignText: "center",
        speakersPosition: "left/top", speakersWidth: "50%",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["38qXQtVhHd", "pmPKOgbSr5", "Qdir6Cgch4", "XZyt9N6jEE"],
      linkedNodes: { headline: "5MXVh1g-B_", header: "pxzVlxTtpG", text: "ZfMg8gZZv6" },
    },
    "5MXVh1g-B_": tc("EbteG5ufZa", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    pxzVlxTtpG: tc("EbteG5ufZa", { isVisible: true, text: td("speakerHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    ZfMg8gZZv6: tc("EbteG5ufZa", { isVisible: true, text: td("speakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    "38qXQtVhHd": speakerChild("EbteG5ufZa", 0),
    pmPKOgbSr5: speakerChild("EbteG5ufZa", 1),
    Qdir6Cgch4: speakerChild("EbteG5ufZa", 2),
    XZyt9N6jEE: speakerChild("EbteG5ufZa", 3),

    // ── Testimonials ──
    v9xUhfuvdA: {
      type: { resolvedName: "Testimonials" }, isCanvas: true,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: false, textSpacing: "space-y-3",
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("testimonialsHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("testimonialsText"), fontSizeText: "text-md", textAlignText: "center",
        testimonialPosition: "left/top", testimonialWidth: "50%",
      },
      displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
      parent: B, hidden: false, nodes: ["XKBYW88Hft", "kDltGciBvz", "IrMCkhbkub", "vE38fPlq4l"],
      linkedNodes: { headline: "hRUoDAhE2r", header: "a-qPj4ONWa", text: "BFrIZf2RL9" },
    },
    hRUoDAhE2r: tc("v9xUhfuvdA", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    "a-qPj4ONWa": tc("v9xUhfuvdA", { isVisible: true, text: td("testimonialsHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    BFrIZf2RL9: tc("v9xUhfuvdA", { isVisible: true, text: td("testimonialsText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    XKBYW88Hft: testimonialChild("v9xUhfuvdA", 0),
    kDltGciBvz: testimonialChild("v9xUhfuvdA", 1),
    IrMCkhbkub: testimonialChild("v9xUhfuvdA", 2),
    vE38fPlq4l: testimonialChild("v9xUhfuvdA", 3),

    // ── Image ──
    wrJx_nHRZi: {
      type: { resolvedName: "Image" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN,
        padding: PAD, textSpacing: "space-y-3", buttonTextSpacing: "mt-6", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("imageHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("imageText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("imageButton"), isVisibleButton: true, buttonAlign: "center",
        buttonBorder: true, buttonUnderline: false,
        imageRounded: "rounded-lg", imageWidth: "50%", imagePosition: "right/bottom",
        isVisibleImage: true, imageSrc: `${A}/shared/image.jpg`, imageAspectRatio: "aspect-video",
      },
      displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "fSehniQAy2", header: "JoM6dBFFvV", text: "CAZjwAG78o", button: "Ih8qInvJiC", image: "wrn53qwbRg" },
    },
    fSehniQAy2: tc("wrJx_nHRZi", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    JoM6dBFFvV: tc("wrJx_nHRZi", { isVisible: true, text: td("imageHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    CAZjwAG78o: tc("wrJx_nHRZi", { isVisible: true, text: td("imageText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    Ih8qInvJiC: btn("wrJx_nHRZi", { text: td("imageButton"), displayName: "button" }),
    wrn53qwbRg: {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: `${A}/shared/image.jpg`, isVisible: true, aspectRatio: "aspect-video", rounded: "rounded-lg", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
      displayName: "ImageChild", custom: { icon: "IconSection" },
      parent: "wrJx_nHRZi", hidden: false, nodes: [], linkedNodes: {},
    },

    // ── Gallery ──
    kANaBb_u2g: {
      type: { resolvedName: "Gallery" }, isCanvas: true,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: false, buttonAlign: "center", textSpacing: "space-y-3", buttonTextSpacing: "mt-6",
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: false, textHeader: td("galleryHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: false, textText: td("galleryText"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("gallerySecondaryButton"), isVisibleSecondaryButton: false,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("galleryPrimaryButton"), isVisiblePrimaryButton: false,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        galleryPosition: "left/top", galleryWidth: "100%",
      },
      displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
      parent: B, hidden: false,
      nodes: ["xQJLrIhYr4", "j3yCjyBos-", "gA-Oq6J-Qj", "KiotWgV7Ai", "T0uiqLZsh6", "w5l3aMCIIR", "SC2RgOTeM0", "VkweOydXTl", "mnvcyxbaye"],
      linkedNodes: { headline: "H4O7BmA3US", header: "WKELPKB2cJ", text: "BMClWAVEMD", secondary: "pfEEiSiImo", primary: "5EQKBdO8W5" },
    },
    H4O7BmA3US: tc("kANaBb_u2g", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    WKELPKB2cJ: tc("kANaBb_u2g", { isVisible: false, text: td("galleryHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    BMClWAVEMD: tc("kANaBb_u2g", { isVisible: false, text: td("galleryText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    pfEEiSiImo: btn("kANaBb_u2g", { type: "secondary", text: td("gallerySecondaryButton"), isVisible: false, displayName: "ButtonChild" }),
    "5EQKBdO8W5": btn("kANaBb_u2g", { text: td("galleryPrimaryButton"), isVisible: false, displayName: "ButtonChild" }),
    xQJLrIhYr4: galleryImage("kANaBb_u2g", "02"),
    "j3yCjyBos-": galleryImage("kANaBb_u2g", "03"),
    "gA-Oq6J-Qj": galleryImage("kANaBb_u2g", "06"),
    KiotWgV7Ai: galleryImage("kANaBb_u2g", "07"),
    T0uiqLZsh6: galleryImage("kANaBb_u2g", "08"),
    w5l3aMCIIR: galleryImage("kANaBb_u2g", "10"),
    SC2RgOTeM0: galleryImage("kANaBb_u2g", "01"),
    VkweOydXTl: galleryImage("kANaBb_u2g", "04"),
    mnvcyxbaye: galleryImage("kANaBb_u2g", "05"),

    // ── Numbers ──
    aFVOnX6KJc: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN,
        padding: PAD, textSpacing: "space-y-3", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("numbersHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("numbersText"), fontSizeText: "text-md", textAlignText: "center",
        numbersPosition: "left/top", numbersWidth: "50%",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["JNwBpaXs9q", "H22YgPYbR3", "i_JNTWoar7", "WAKDBcs9et"],
      linkedNodes: { headline: "xs-X1tNSPU", header: "knyjtNekjf", text: "Qd04Ws7F1w" },
    },
    "xs-X1tNSPU": tc("aFVOnX6KJc", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    knyjtNekjf: tc("aFVOnX6KJc", { isVisible: true, text: td("numbersHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    Qd04Ws7F1w: tc("aFVOnX6KJc", { isVisible: true, text: td("numbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    JNwBpaXs9q: numbersChild("aFVOnX6KJc", 0),
    H22YgPYbR3: numbersChild("aFVOnX6KJc", 1),
    i_JNTWoar7: numbersChild("aFVOnX6KJc", 2),
    WAKDBcs9et: numbersChild("aFVOnX6KJc", 3),

    // ── FaQ ──
    BR_UaZSeQm: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-4", isVisible: true,
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("faqHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("faqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["1ycabPxULM", "YTocdBbuGn", "oskIBRNdul", "I63fQpjfSh", "Vts04r9edV"],
      linkedNodes: { headline: "wNQhgkOUK2", header: "ulECDEOW3K", text: "4tWxHLTE1c" },
    },
    wNQhgkOUK2: tc("BR_UaZSeQm", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    ulECDEOW3K: tc("BR_UaZSeQm", { isVisible: true, text: td("faqHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    "4tWxHLTE1c": tc("BR_UaZSeQm", { isVisible: true, text: td("faqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    "1ycabPxULM": faqChild("BR_UaZSeQm", 0),
    YTocdBbuGn: faqChild("BR_UaZSeQm", 1),
    oskIBRNdul: faqChild("BR_UaZSeQm", 2),
    I63fQpjfSh: faqChild("BR_UaZSeQm", 3),
    Vts04r9edV: faqChild("BR_UaZSeQm", 4),

    // ── Map ──
    OutHHVpX4o: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN,
        padding: PAD, isVisible: false, textSpacing: "space-y-3", buttonTextSpacing: "mt-6",
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("mapHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("mapText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("mapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        mapRounded: "rounded-lg", mapWidth: "50%", mapPosition: "right/bottom",
        mapZoom: 10, mapAspectRatio: "aspect-video", isVisibleMap: true, mapLocation: td("defaultMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "VgETCPSdDA", header: "znKGtO9tbV", text: "2QleybKgdn", button: "0WScLIAqmw" },
    },
    VgETCPSdDA: tc("OutHHVpX4o", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    znKGtO9tbV: tc("OutHHVpX4o", { isVisible: true, text: td("mapHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    "2QleybKgdn": tc("OutHHVpX4o", { isVisible: true, text: td("mapText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    "0WScLIAqmw": btn("OutHHVpX4o", { text: td("mapButton"), displayName: "button" }),

    // ── Video ──
    "5RlDSMNjqM": {
      type: { resolvedName: "Video" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN,
        padding: PAD, isVisible: false, textSpacing: "space-y-3", buttonTextSpacing: "mt-6",
        isVisibleHeadline: false, textHeadline: td("welcomeHeadline"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("videoHeader"), fontSizeHeader: "text-xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("videoText"), fontSizeText: "text-md", textAlignText: "center",
        buttonSize: "medium", buttonText: td("videoButton"), isVisibleButton: false, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
        videoRounded: "rounded-lg", videoWidth: "50%", videoAspectRatio: "aspect-video",
        videoPosition: "right/bottom", isVisibleVideo: true, videoUrl: "https://youtu.be/sdESKm-nKQE",
      },
      displayName: "Video", custom: { icon: "IconVideo", preview: pv("Video-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "QqlvHWw3sW", header: "ml8-2r1Gto", text: "v_oyBwmAuf", button: "yvRYduN5PK" },
    },
    QqlvHWw3sW: tc("5RlDSMNjqM", { isVisible: false, text: td("welcomeHeadline"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    "ml8-2r1Gto": tc("5RlDSMNjqM", { isVisible: true, text: td("videoHeader"), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true }),
    v_oyBwmAuf: tc("5RlDSMNjqM", { isVisible: true, text: td("videoText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    yvRYduN5PK: btn("5RlDSMNjqM", { text: td("videoButton"), isVisible: false, displayName: "button" }),
  });
};

export const login = (td: TranslationFn, hasRegisterPage = true) => {
  const LOG = "XLz6rxNrMv";
  const LC = "LogChild_01";
  return enc({
    ...scaffold({ ab: "RxZ0lFmfmj", abs: "n19bFhEAeb", body: "vvbhbSfsE2", ft: "ZQbYCFx8MD", fts: "NaxjUg9SxJ" }, [LOG], false),
    [LOG]: {
      type: { resolvedName: "Login" }, isCanvas: false, props: AUTH_WRAPPER_PROPS,
      displayName: "Login", custom: { icon: "IconForms", preview: pv("Login-preview") },
      parent: "vvbhbSfsE2", hidden: false, nodes: [], linkedNodes: { loginChild: LC },
    },
    [LC]: {
      type: { resolvedName: "LoginChild" }, isCanvas: true, props: AUTH_CHILD_PROPS,
      displayName: "LoginChild", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: LOG, hidden: false, nodes: [],
      linkedNodes: { headline: "as_2PViRgz", header: "cmC1J8qxap", text: "q0ELbWonPX", loginForm: "avtGG3lSfn", codeForm: "U5nJXZBg_w", primary: "fxjI93m12g", secondary: "0zjE6q451X" },
    },
    as_2PViRgz: tc(LC, { isVisible: false, text: td("loginHeadline"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    cmC1J8qxap: tc(LC, { isVisible: true, text: td("loginHeadline"), fontSize: "text-xl", textAlign: "left", displayName: "header", header: true }),
    q0ELbWonPX: tc(LC, { isVisible: true, text: td("enterDetailsText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    avtGG3lSfn: fi(LC, { label: td("username"), requiredField: true, inputFieldVariable: "username", changeable: true }),
    U5nJXZBg_w: fi(LC, { label: td("code"), requiredField: true, inputFieldVariable: "code", changeable: true }),
    fxjI93m12g: btn(LC, { linkType: "internal", linkUrl: "", text: td("signIn"), widthMode: "full", buttonType: "submit", displayName: "buttonOne", fixedNavigation: false }),
    "0zjE6q451X": btn(LC, { linkType: "internal", type: "secondary", linkUrl: "/register", text: td("dontHaveAccount"), widthMode: "full", underline: true, border: false, displayName: "buttonTwo", fixedNavigation: true, isVisible: hasRegisterPage }),
  });
};
